import { getSettings } from './store'

/**
 * Install prompt and the nightly reminder.
 *
 * The reminder is intentionally modest. A web app cannot wake a sleeping phone
 * reliably without a push server, and pretending otherwise would mean someone
 * relies on a nudge that never comes. What this does is fire while the app is
 * open or backgrounded, and the Telegram bot in `bot/` covers the case where
 * the phone is locked. If someone needs a guaranteed 10pm ping, the bot is the
 * honest answer, not this.
 */

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

let deferred: BeforeInstallPromptEvent | null = null

window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault()
  deferred = e as BeforeInstallPromptEvent
})

window.addEventListener('appinstalled', () => {
  deferred = null
})

export function canPrompt(): boolean {
  return deferred !== null
}

export async function promptInstall(): Promise<boolean> {
  if (!deferred) return false
  await deferred.prompt()
  const { outcome } = await deferred.userChoice
  deferred = null
  return outcome === 'accepted'
}

export function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as unknown as { standalone?: boolean }).standalone === true
  )
}

export type NotifState = 'unsupported' | 'default' | 'granted' | 'denied'

export function notificationState(): NotifState {
  if (!('Notification' in window)) return 'unsupported'
  return Notification.permission as NotifState
}

export async function requestNightReminder(): Promise<NotifState> {
  if (!('Notification' in window)) return 'unsupported'
  if (Notification.permission === 'default') await Notification.requestPermission()
  const state = Notification.permission as NotifState
  if (state === 'granted') scheduleNextReminder()
  return state
}

let reminderTimer: number | undefined

/** Schedules a single timeout for the next ritual time, then reschedules itself. */
export function scheduleNextReminder(): void {
  clearTimeout(reminderTimer)
  if (notificationState() !== 'granted') return

  const [hh, mm] = getSettings().ritualTime.split(':').map(Number)
  const next = new Date()
  next.setHours(hh ?? 22, mm ?? 0, 0, 0)
  if (next.getTime() <= Date.now()) next.setDate(next.getDate() + 1)

  const delay = next.getTime() - Date.now()
  // setTimeout is capped at ~24.8 days, and we never schedule further than a
  // day out, so a single timeout is safe here.
  reminderTimer = window.setTimeout(() => {
    fireReminder()
    scheduleNextReminder()
  }, delay)
}

function fireReminder(): void {
  const lang = getSettings().lang
  const title = lang === 'fa' ? 'نابوسو' : 'NabuSu'
  const body =
    lang === 'fa'
      ? 'وقتش است. ده دقیقه، یک در را انتخاب کن.'
      : 'It is time. Ten minutes — pick a door.'
  try {
    new Notification(title, { body, icon: '/icons/icon-192.png', tag: 'nabusu-ritual' })
  } catch {
    /* Some browsers only allow notifications from a service worker; skip quietly. */
  }
}
